import {
    Alignment, Collection, Color, DynamicLoader, ExtendedDataType, getThemeColor, MixinUtil, ModelRow, StringUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { CaptionLabel } from "../label/CaptionLabel";
import { Component, Image, Label, LabelProps } from "../..";
import { CaptionedMixin } from "../../mixins/CaptionedMixin";
import { CaptionedMixinInterface } from "../../mixins/CaptionedMixinInterface";
import { getCurrentDataSourceMode, getRelevantModelRow } from "../../base/ComponentDataLink";
import { ComponentPropDefinition, ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ListenerListDef } from "../../base/ListenerListDef";
import { PrintableEvent } from "../../events/PrintableEvent";
import { PrintableMixin } from "../../mixins/PrintableMixin";
import { PrintableMixinInterface } from "../../mixins/PrintableMixinInterface";
import { DataSourceMode } from "../../databinding/DataSource";
import { ChangeEvent, ChangeListener } from "../../events/ChangeEvent";
import { DomEvent } from "../../events/DomEvent";
import { ImageProps } from "../image/ImageProps";
import { PrintableLabel } from "../label/PrintableLabel";
import { SwitchPropDefinitions, SwitchProps } from "./SwitchProps";
import { SwitchStyles } from "./SwitchStyles";

const _changeListenerDef: ListenerListDef = { listName: "_changeListeners" };

type SwitchValueType = string | boolean | number;

export class Switch extends Component implements SwitchProps {
    private _switchRow: HTMLDivElement;
    private _switchContainer: HTMLDivElement;
    private _track: HTMLSpanElement;
    private _thumb: HTMLDivElement;
    private _thumbInner: HTMLDivElement;
    private _captionAlignment: Alignment.LEFT | Alignment.TOP;
    private _checked: boolean;
    private _allowIndeterminate: boolean = false;
    private _nextSearchValue: boolean = true;
    private _leftImageName: ImageName;
    private _leftImageProps: Partial<ImageProps>;
    private _leftLabel: Label;
    private _leftValue: SwitchValueType;
    private printableLabel: PrintableLabel;
    private _rightImageName: ImageName;
    private _rightImageProps: Partial<ImageProps>;
    private _rightLabel: Label;
    private _rightValue: SwitchValueType;
    private _captionVisible: boolean;
    private _defaultDataValue: boolean;
    private _checkedTrackColor: Color;

    constructor(props?: Partial<SwitchProps>) {
        super("div", props);
        this.setupElements(props);
        this.initCheckedToFalse();
        this.setProps(props);
        this.supportsEditSecurity = true;
    }

    override get defaultDataValue(): boolean {
        return this._defaultDataValue;
    }

    public set defaultDataValue(value: boolean) {
        this._defaultDataValue = value;
    }

    private setupElements(props: Partial<SwitchProps>) {
        this._switchRow = document.createElement("div");
        this._switchRow.classList.add(SwitchStyles.root);
        this.captionLabel = new CaptionLabel(this);
        this._leftLabel = new Label({ text: props?.leftCaption, themeKey: "switch.captions", fontBold: true, color: getThemeColor("subtle.darkest") });
        this._rightLabel = new Label({ text: props?.rightCaption, themeKey: "switch.captions", color: getThemeColor("subtle.darkest") });
        this._switchContainer = document.createElement("div");
        this._switchContainer.classList.add(SwitchStyles.switchContainer);
        this._track = document.createElement("span");
        this._track.setAttribute("tabindex", "0");
        this._track.classList.add(SwitchStyles.track);
        this._thumb = document.createElement("div");
        this._thumb.classList.add(SwitchStyles.thumb);
        this._thumbInner = document.createElement("div");
        this._thumbInner.classList.add(SwitchStyles.thumbInner);
        this._addTrackListeners();
        this._element.appendChild(this.captionLabel._element);
        this._element.appendChild(this._switchRow);
        this._thumb.appendChild(this._thumbInner);
        this._switchContainer.appendChild(this._track);
        this._track.appendChild(this._thumb);
        this._switchRow.appendChild(this._switchContainer);
        this.syncLeftCaptionLabel();
        this.syncRightCaptionLabel();
    }

    override setProps(props: Partial<SwitchProps>) {
        super.setProps(props);
    }

    public override get tooltipAnchor(): HTMLElement {
        if (this.printableLabel != null) {
            return this.printableLabel.tooltipAnchor;
        }
        else {
            return this._switchRow;
        }
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this._leftLabel, "leftLabel");
        this.setInnerComponentId(this._rightLabel, "rightLabel");
        this.setInnerComponentId(this._switchContainer, "switchContainer");
        this.setInnerComponentId(this._track, "track");
        this.setInnerComponentId(this._thumb, "thumb");
        this.setInnerComponentId(this._thumbInner, "thumbInner");
        this.setInnerComponentId(this._switchRow, "switchRow");
        this.captionLabel?.syncId();
        this.printableLabel?.syncId();
    }

    toggle(event: Event) {
        if (this._designer != null) {
            return;
        }
        if (this._allowIndeterminate !== true) {
            this._internalSetChecked(!this.checked, event);
        }
        else {
            if (this.checked !== undefined) {
                this._nextSearchValue = !this.checked;
                this._internalSetChecked(undefined, event);
            }
            else {
                this._internalSetChecked(this._nextSearchValue, event);
            }
        }
    }

    /**
     * We need to init the switch to the left position (checked = false), but we don't want to fire off change listeners
     * or change bound data.  So call _internalSetChecked() with an override that skips those actions.
     */
    private initCheckedToFalse() {
        this._internalSetChecked(false, null, true);
    }

    get checked() {
        return this._checked;
    }

    set checked(value) {
        this._internalSetChecked(value, null);
    }

    _setCheckedProps(value: boolean | null) {
        const isChecked = value === true;
        const isUnchecked = value === false;
        const isIndeterminate = this._allowIndeterminate && value == null;

        this._thumb.classList.toggle(SwitchStyles.neutral, isIndeterminate);
        this._thumb.classList.toggle(SwitchStyles.checked, isChecked);

        this._rightLabel.fontBold = isChecked;
        this._leftLabel.fontBold = isUnchecked;

        this._element.setAttribute("automation-state", isIndeterminate ? "neutral": isChecked.toString());

        this.syncTrackColor();
    }

    private syncTrackColor() {
        this._track.style.backgroundColor = getThemeColor(this.checked ? this.checkedTrackColor : "subtle");
    }

     _internalSetChecked(value: boolean, originatingEvent: DomEvent, initialSetup: boolean = false) {
        if (this._checked === value)
            return;
        const oldValue = this._checked;
        this._checked = value;
        if (initialSetup !== true) {
            this._internalUpdateBoundData();
            this.storeUserChoiceIfRemembered();
            const event = new ChangeEvent(this, oldValue, value, originatingEvent);
            this.fireListeners(_changeListenerDef, event)
        }
        this._setCheckedProps(value);
        this.handleImage();

        //this seems gross; the checked value can be set from the data after we've already set printable = true
        //so we have to update the printable's label here too (if it exists)
        if (this.printableLabel != null) {
            if (value)
                this.printableLabel.text = this._rightLabel.text;
            else
                this.printableLabel.text = this._leftLabel.text;
        }
    }

    get valueAsString(): string {
        return this.checked.toString();
    }

    set valueAsString(value: string) {
        this.checked = value === "true";
    }

    get caption(): string {
        return this.super_get_caption();
    }

    set caption(value: string) {
        if (this.captionValueMatches(value) === true) {
            return;
        }
        const oldValue = this.caption;
        this.super_set_caption(value);
        this.syncCaption();
        this._matchIdToValue(oldValue, value);
    }

    get captionLabelProps(): Partial<LabelProps> {
        return {
            ...this.super_get_captionLabelProps(),
            marginBottom: 4
        };
    }

    set captionLabelMarginTop(value: number) {
        this.captionLabel.marginTop = value;
    }

    set captionLabelMarginBottom(value: number) {
        this.captionLabel.marginBottom = value;
    }

    syncCaption() {
        if (this.caption == null) {
            //a switch doesn't need the required * in front the caption if there is no caption; the switch will always have a value
            this.captionLabel.text = "";
        }
        else {
            this.captionLabel.text = this.getPrefixedCaption();
        }
    }

    get leftCaption() {
        return this._leftLabel.text;
    }

    set leftCaption(value) {
        this._leftLabel.text = value;
        this.syncLeftCaptionLabel();
    }

    get rightCaption() {
        return this._rightLabel.text;
    }

    set rightCaption(value) {
        this._rightLabel.text = value;
        this.syncRightCaptionLabel();
    }

    set captionAlignment(value: Alignment.LEFT | Alignment.TOP) {
        this._captionAlignment = value;
        if (value === Alignment.LEFT) {
            this._element.style.display = "flex";
            this._element.style.flexDirection = "row";
            this._element.style.alignItems = "center";
            this.captionLabel.marginRight = "auto";
            this.captionLabel.marginBottom = 0;
        }
        else {
            this._element.style.display = "unset";
            this._element.style.alignItems = "";
            this.captionLabel.marginRight = 0;
            this.captionLabel.marginBottom = 4;
        }
    }

    get captionAlignment(): Alignment.LEFT | Alignment.TOP {
        if (this._captionAlignment == null)
            return Alignment.TOP;
        else
            return this._captionAlignment;
    }

    private syncLeftCaptionLabel() {
        if (StringUtil.isEmptyString(this._leftLabel.text) === false) {
            this._switchContainer.style.marginLeft = "8px";
            if (this._switchRow.contains(this._leftLabel._element) === false)
                this._switchRow.insertBefore(this._leftLabel._element, this._switchContainer);
        }
        else {
            this._switchContainer.style.marginLeft = "";
            if (this._switchRow.contains(this._leftLabel._element) === true)
                this._switchRow.removeChild(this._leftLabel._element);
        }
    }

    private syncRightCaptionLabel() {
        if (StringUtil.isEmptyString(this._rightLabel.text) === false) {
            this._switchContainer.style.marginRight = "8px";
            if (this._switchRow.contains(this._rightLabel._element) === false)
                this._switchRow.insertBefore(this._rightLabel._element, this._switchContainer.nextSibling);
        }
        else {
            this._switchContainer.style.marginRight = "";
            if (this._switchRow.contains(this._rightLabel._element) === true)
                this._switchRow.removeChild(this._rightLabel._element);
        }
    }

    get leftImageName(): ImageName {
        return this._leftImageName;
    }

    set leftImageName(value: ImageName) {
        this._leftImageName = value;
        this.handleImage();
    }

    get leftImageProps(): Partial<ImageProps> {
        return this._leftImageProps;
    }

    set leftImageProps(value: Partial<ImageProps>) {
        this._leftImageProps = value;
        this.handleImage();
    }

    get rightImageName() {
        return this._rightImageName;
    }

    set rightImageName(value) {
        this._rightImageName = value;
        this.handleImage();
    }

    get rightImageProps(): Partial<ImageProps> {
        return this._rightImageProps;
    }

    set rightImageProps(value: Partial<ImageProps>) {
        this._rightImageProps = value;
        this.handleImage();
    }

    handleImage() {
        //let image: Image;
        // if (this._allowIndeterminate === true && this.checked === undefined) {
        //   image = new Image({ name: ImageName.dash, maxWidth: 14, marginTop: -2, color: "primary.reverse", ...this.rightImageProps });
        // }
        // else {
        const image = this.createImage();
        //}
        const child = this._thumbInner.firstChild;
        if (image != null) {
            if (child == null)
                this._thumbInner.appendChild(image._element);
            else
                this._thumbInner.replaceChild(image._element, child);
        }
        else if (child != null)
            this._thumbInner.innerHTML = "";
    }

    private createImage() {
        const imageName = this.checked ? this.rightImageName : this.leftImageName;
        const imageProps = this.checked ? this.rightImageProps : this.leftImageProps;
        if (imageName != null || imageProps) {
            const idSuffix = this.checked ? "rightImage" : "leftImage";
            return new Image({
                name: imageName,
                maxWidth: 14, marginTop: -2, color: "primary.reverse",
                ...imageProps,
                id: this.createInnerComponentId(idSuffix)
            });
        }
        return null;
    }

    addChangeListener(value: ChangeListener) {
        this.addEventListener(_changeListenerDef, value);
    }

    removeChangeListener(value: ChangeListener) {
        this.removeEventListener(_changeListenerDef, value);
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return SwitchPropDefinitions.getDefinitions();
    }

    override getSearchValues(): string[] {
        const result = [];
        result.push(this._checked ? this._rightLabel.text : this._leftLabel.text);
        return result;
    }

    get leftValue(): SwitchValueType {
        return this._leftValue;
    }

    set leftValue(value: SwitchValueType) {
        this._leftValue = value;
    }

    get rightValue(): SwitchValueType {
        return this._rightValue;
    }

    set rightValue(value: SwitchValueType) {
        this._rightValue = value;
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (data == null || this.field == null) {
            this.checked = false;
            return;
        }

        const value = data != null ? ((data instanceof ModelRow) ? data.get(this.field) : data[this.field]) : null;
        if (value == null && this.allowIndeterminate === true) {
            this.checked = undefined;
            return;
        }
        if (typeof value === "string") {
            this._setCheckedFromString(value);
        }
        else if (this.rightValue != null) {
            this.checked = value === this.rightValue;
        }
        else if (this.leftValue != null) {
            this.checked = value !== this.leftValue;
        }
        else {
            this.leftValue = false;
            this.rightValue = true;
            this.checked = value === this.rightValue;
        }
    }

    private _setCheckedFromString(value: string) {
        if (value === "Y" || value === "N") {
            if (this.leftValue == null) {
                this.leftValue = "N";
            }
            if (this.rightValue == null) {
                this.rightValue = "Y";
            }
            this.checked = this.rightValue === value;
        }
        else if (value === "true" || value === "false") {
            if (this.leftValue == null) {
                this.leftValue = false;
            }
            if (this.rightValue == null) {
                this.rightValue = true;
            }
            this.checked = value === "true";
        }
        else if (this.rightValue != null) {
            this.checked = value === this.rightValue;
        }
        else if (this.leftValue != null) {
            this.checked = value !== this.leftValue;
        }
        else {
            this.leftValue = false;
            this.rightValue = true;
            this.checked = false;
        }
        this._setCheckedProps(this.checked);
    }


    protected override _fieldBindingChanged(): void {
        super._fieldBindingChanged();
        if ((this.leftValue != null && this.rightValue != null)) {
            return;
        }
        if (this._boundField?.extDataType === ExtendedDataType.YES_NO) {
            this.leftValue = "N";
            this.rightValue = "Y";
        }
    }

    private _internalUpdateBoundData() {
        const row = getRelevantModelRow(this);
        const mode = getCurrentDataSourceMode(this);
        this.updateBoundData(row, mode);
    }

    override updateBoundData(row: ModelRow, mode: DataSourceMode) {
        if (this.field != null && row != null) {
            if (this.checked === true)
                row.set(this.field, this.rightValue, this);
            else if (this.checked === false)
                row.set(this.field, this.leftValue, this);
        }
    }

    override dataSourceModeChanged(mode: DataSourceMode) {
        super.dataSourceModeChanged(mode);
        if (mode === DataSourceMode.SEARCH) {
            this._allowIndeterminate = true;
            this._nextSearchValue = true;
            this.checked = undefined;
        } else {
            this._allowIndeterminate = false;
        }
        this.syncPrintable();
    }

    protected _applyPrintable(value: boolean) {
        if (value) {
            if (this.printableLabel == null && this._element.contains(this._switchRow)) {
                this._element.removeChild(this._switchRow);
                // Cannot instantiate using class directly, causes circular reference
                const printableLabelClass = DynamicLoader.getClassForPath("components/components/label/PrintableLabel");
                this.printableLabel = new printableLabelClass(this, {
                    padding: 0,
                    text: this._checked ? this._rightLabel.text : this._leftLabel.text,
                });
                this._element.appendChild(this.printableLabel._element);
            }
        }
        else {
            if (this.printableLabel != null && this._element.contains(this.printableLabel._element))
                this._element.removeChild(this.printableLabel._element);
            this.printableLabel = null;
            this._element.appendChild(this._switchRow);
            this._syncEnabled();
        }

        this.firePrintableListeners(new PrintableEvent(this, this.printableLabel));
    }

    get allowIndeterminate(): boolean {
        return this._allowIndeterminate;
    }

    set allowIndeterminate(value: boolean) {
        this._allowIndeterminate = value;
    }

    get captionVisible(): boolean {
        return this._captionVisible == null ? true : this._captionVisible;
    }

    set captionVisible(value: boolean) {
        this._captionVisible = value;
        const contains = this._element.contains(this.captionLabel._element);
        if (value && !contains)
            this._element.insertBefore(this.captionLabel._element, this._switchRow);
        else if (!value && contains)
            this._element.removeChild(this.captionLabel._element);
        if (value)
            this._element.classList.remove(SwitchStyles.noCaption);
        else
            this._element.classList.add(SwitchStyles.noCaption);
    }

    public get checkedTrackColor(): Color {
        return this._checkedTrackColor ?? this.getPropertyDefinitions().checkedTrackColor.defaultValue;
    }

    public set checkedTrackColor(value: Color) {
        this._checkedTrackColor = value;
        this.syncTrackColor();
    }

    protected _getDefaultEventProp(): string {
        return "onChange";
    }

    override get serializationName() {
        return "switch";
    }

    override get properName(): string {
        return "Switch";
    }

    public override getEventTarget(): HTMLElement {
        return this._switchContainer;
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "change": { ..._changeListenerDef },
            ...MixinUtil.getMixinListenerDefs(this)
        };
    }

    override getBasicValue(): any {
        return this.checked;
    }

    override _applyEnabled(value: boolean): void {
        this._leftLabel.enabled = value;
        this._rightLabel.enabled = value;
        if (value) {
            this._element.removeAttribute("disabled");
            this._track.removeAttribute("disabled");
            this._track.setAttribute("tabindex", "0");
            this._thumb.classList.remove(SwitchStyles.thumbDisabled);
            this._thumbInner.classList.remove(SwitchStyles.thumbInnerDisabled);
            this._thumb.classList.add(SwitchStyles.thumb);
            this._thumbInner.classList.add(SwitchStyles.thumbInner);
            this._addTrackListeners();
            this._setCheckedProps(this.checked);
        }
        else {
            this._element.setAttribute("disabled", "true");
            this._track.setAttribute("disabled", "true");
            this._track.setAttribute("tabindex", "-1");
            this._thumb.classList.remove(SwitchStyles.thumb);
            this._thumbInner.classList.remove(SwitchStyles.thumbInner);
            this._thumb.classList.add(SwitchStyles.thumbDisabled);
            this._thumbInner.classList.add(SwitchStyles.thumbInnerDisabled);
            this._clearTrackListeners();
        }

        this._track.style.opacity = value ? "" : "0.5";
    }

    private _addTrackListeners() {
        if (this._track.onclick == null) {
            this._track.onclick = event => {
                this.toggle(event);
            };
        }
        if (this._track.onkeydown == null) {
            this._track.onkeydown = (event) => {
                if (event.key === " ") {
                    this.toggle(event);
                    event.preventDefault();
                }
            };
        }
    }

    private _clearTrackListeners() {
        this._track.onclick = null;
        this._track.onkeydown = null;
    }

    protected _initialDropInDesigner() {
        this.leftCaption = "No";
        this.rightCaption = "Yes";
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "leftValue" && this._boundField?.extDataType === ExtendedDataType.YES_NO)
            return "N";
        else if (prop.name === "rightValue" && this._boundField?.extDataType === ExtendedDataType.YES_NO) {
            return "Y";
        }
        return super.determinePropertyDefaultValue(prop);
    }

    /**
     * For a Switch, display the props access label to the right of the switch/track element.
     */
    displayPropsAccessLabel(label: Label) {
        this._switchRow.appendChild(label._element);
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(Switch, [ CaptionedMixin, PrintableMixin ]);

// TypeScript interface declaration for mixin integration
export interface Switch extends CaptionedMixinInterface, PrintableMixinInterface {
}

ComponentTypes.registerComponentType("switch", {
    constructor: Switch.prototype.constructor
});
